import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateEmployeeNoteInput,
  UpdateEmployeeNoteInput,
} from '@/types/employee-note';
import {
  getEmployeeNotes,
  createEmployeeNote,
  updateEmployeeNote,
  deleteEmployeeNote,
} from '@/lib/employees';

// Query keys
export const employeeNotesKeys = {
  all: ['employee-notes'] as const,
  byEmployee: (employeeId: string) => ['employee-notes', employeeId] as const,
};

/**
 * Hook to fetch employee notes
 */
export function useEmployeeNotes(employeeId: string) {
  return useQuery({
    queryKey: employeeNotesKeys.byEmployee(employeeId),
    queryFn: () => getEmployeeNotes(employeeId),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a new employee note
 */
export function useCreateEmployeeNote(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEmployeeNoteInput) => createEmployeeNote(employeeId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeNotesKeys.byEmployee(employeeId) });
    },
  });
}

/**
 * Hook to update an employee note
 */
export function useUpdateEmployeeNote(employeeId: string, noteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateEmployeeNoteInput) =>
      updateEmployeeNote(employeeId, noteId, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeNotesKeys.byEmployee(employeeId) });
    },
  });
}

/**
 * Hook to delete an employee note
 */
export function useDeleteEmployeeNote(employeeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) => deleteEmployeeNote(employeeId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeNotesKeys.byEmployee(employeeId) });
    },
  });
}
